import { forwardRef, useEffect, useState } from 'react';
import { getFieldComponent } from '@mfe-helpers/fieldComponent';
import { getIncrementValues } from '@mfe-helpers/rangeValue';
import { isChromiumBased } from '@mfe-helpers/userAgent';
import { useAppContext } from '@mfe-presentation/contexts';
import useFieldUtils from '@mfe-presentation/hooks/useFieldUtils';
import { TStyleProps } from '@mfe-types/box';
import { TChangeEvent } from '@mfe-types/event';
import { Field, IFieldConfiguration } from '@mfe-types/field';
import { IReactHookFormErrorContent } from '@mfe-types/validator';
import { useController, useFormContext, useWatch } from 'react-hook-form';

interface IInputFieldWrapper {
    field: Field;
    name: string;
    styles?: TStyleProps;
    hidePlaceholder?: boolean;
    translationParams?: Record<string, any>;
}

const getAutocomplete = (fieldAutocomplete: string | undefined) => {
    const offValue = isChromiumBased(navigator.userAgent) ? 'chrome-off' : 'off';
    return fieldAutocomplete ? 'on' : offValue;
};

const getComponentStyles = (field: Field, styles?: TStyleProps): TStyleProps => ({
    width: '100%',
    marginBottom: 'spacing.24',
    ...(field.styles?.marginTop && { marginTop: 'spacing.24' }),
    ...(field.styles?.fontSize === 'small' && { fontSize: '14px' }),
    ...styles,
});

const FieldWrapper = forwardRef(
    ({ name, field, styles, hidePlaceholder, translationParams }: IInputFieldWrapper, ref) => {
        const { control, resetField, trigger, setValue } = useFormContext();
        const {
            config,
            hasSubmittedCurrentStep,
            hasSubmittedPriceEstimateFeedback,
            hasUpdatedPhoneNumber,
        } = useAppContext();
        const watcher = useWatch({ control });
        const [canDisplayError, setCanDisplayError] = useState<boolean>(false);

        const { field: controlledField, fieldState } = useController({
            control,
            name,
        });

        const {
            isDisabled,
            isDisplayed,
            options,
            translations,
            error,
            relatedFieldToUpdate,
            relatedFieldToFormat,
            relatedFieldToValidate,
        } = useFieldUtils({
            watcher,
            field,
            name,
            validationError: fieldState.error as IReactHookFormErrorContent | undefined,
            config,
            translationParams,
        });

        useEffect(() => {
            if (relatedFieldToUpdate && fieldState.isDirty) {
                resetField(relatedFieldToUpdate.fieldName, {
                    defaultValue: controlledField.value,
                });
            }
            if (relatedFieldToFormat && fieldState.isDirty) {
                setValue(relatedFieldToFormat.fieldName, relatedFieldToFormat.formattedValue);
            }
            if (relatedFieldToValidate) {
                trigger(relatedFieldToValidate.fieldName);
            }
        }, [controlledField.value]);

        useEffect(() => {
            if (
                fieldState.error &&
                (hasSubmittedCurrentStep ||
                    hasSubmittedPriceEstimateFeedback ||
                    hasUpdatedPhoneNumber)
            ) {
                setCanDisplayError(true);
            }
        }, [fieldState.error, hasSubmittedCurrentStep]);

        useEffect(() => {
            if (!isDisplayed && resetField) {
                resetField(name);
            }
        }, [isDisplayed, resetField]);

        useEffect(() => {
            window.scrollTo(0, 0);
        }, []);

        if (!isDisplayed) return null;

        const incrementValues = getIncrementValues(field, watcher);

        const handleChange = (value: unknown) => {
            const formattedValue = field.formatInputValue(field, value, watcher, incrementValues);
            controlledField.onChange(formattedValue);
            if (field.triggerSideEffect) {
                field.triggerSideEffect(formattedValue);
            }
        };

        const handleBlur = () => {
            controlledField.onBlur();
        };

        const componentProps: IFieldConfiguration<any, any> = {
            ...controlledField,
            onChange: (e: TChangeEvent) => handleChange(e),
            onBlur: handleBlur,
            error: canDisplayError ? error : undefined,
            isDisabled,
            autoComplete: getAutocomplete(field.autocomplete), // TODO: be able to provide a guidance. https://kugawana.slack.com/archives/C048JM75SAC/p1694005390620569
            ref,
            isOptional: !field.isRequired,
            hasNecessityIndicator: false,
            ...(field.inputMode && ({ inputMode: field.inputMode } as any)),
            ...(field.type && ({ type: field.type } as any)),
            ...(field.increment && { increment: field.increment }),
            ...(field.direction && { direction: field.direction }),
        };

        const texts = {
            ...translations,
            placeholder: hidePlaceholder ? '' : translations.placeholder,
        };

        const Component = getFieldComponent(field.uiComponent);

        return (
            <Component
                field={componentProps}
                options={options}
                texts={texts}
                styles={getComponentStyles(field, styles)}
            />
        );
    },
);

export default FieldWrapper;
